import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { confirmSignUp, resendSignUpCode, signIn, signOut, signUp } from 'aws-amplify/auth';
import { useTranslation } from 'react-i18next';
import { useAuthStore } from '@/store/authStore';
import { apiFetch } from '@/services/apiGateway';
import { GlobalHeader } from '@/components/GlobalHeader';
import { PasswordInput } from '@/components/ui/PasswordInput';
import { getRegionI18nKey } from '@/utils/region';
import { COUNTRIES } from '@/constants/education';
import i18n from '@/i18n';

const CONSENT_VERSION = '1.0';

const UNIVERSITY_YEARS = [
  'Licence 1 (L1)',
  'Licence 2 (L2)',
  'Licence 3 (L3)',
  'Master 1 (M1)',
  'Master 2 (M2)',
];

function friendlyError(err: unknown): string {
  const msg = err instanceof Error ? err.message : '';
  const t = i18n.t.bind(i18n);
  if (msg.includes('password') || msg.includes('Password')) return t('register_parent.err_password');
  if (msg.includes('UsernameExistsException') || msg.includes('already')) return t('register_parent.err_exists');
  if (msg.includes('CodeMismatchException') || msg.includes('Invalid verification')) return t('register_parent.err_code_mismatch');
  if (msg.includes('ExpiredCodeException')) return t('register_parent.err_code_expired');
  return msg || t('register_parent.err_generic');
}

export function RegisterUniversityPage() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const init = useAuthStore((s) => s.init);

  const [step, setStep] = useState<1 | 2 | 3>(1);

  // Step 1
  const [email, setEmail]       = useState('');
  const [password, setPassword] = useState('');
  // Step 2
  const [otpCode, setOtpCode]   = useState('');
  // Step 3
  const [displayName, setDisplayName]           = useState('');
  const [country, setCountry]                   = useState('');
  const [schoolYear, setSchoolYear]             = useState('');
  const [consentAi, setConsentAi]               = useState(false);
  const [consentCurriculum, setConsentCurriculum] = useState(false);

  const [isLoading, setIsLoading]     = useState(false);
  const [error, setError]             = useState<string | null>(null);
  const [isResending, setIsResending] = useState(false);
  const [resendMessage, setResendMessage] = useState<string | null>(null);

  // ── Step 1: Cognito sign up ─────────────────────────────────────────────────
  const handleSignUp = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);
    setIsLoading(true);
    try {
      try { await signOut(); } catch { /* clear stale session */ }
      await signUp({
        username: email,
        password,
        options: {
          userAttributes: {
            email,
            'custom:role': 'student',
            'custom:mode': 'university',
          },
        },
      });
      setStep(2);
    } catch (err) {
      const name = err instanceof Error ? (err as { name?: string }).name : undefined;
      if (name === 'UsernameExistsException') {
        // Account exists but may still be UNCONFIRMED (e.g. the original code
        // was never received/entered). resendSignUpCode succeeds only for an
        // unconfirmed account, so use it to disambiguate and, when it works,
        // send the user straight back into the OTP step with a fresh code.
        try {
          await resendSignUpCode({ username: email });
          setResendMessage(t('register_parent.code_resent'));
          setStep(2);
        } catch {
          setError(friendlyError(err));
        }
      } else {
        setError(friendlyError(err));
      }
    } finally {
      setIsLoading(false);
    }
  };

  // ── Step 2: confirm OTP → sign in ───────────────────────────────────────────
  const handleVerify = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);
    setIsLoading(true);
    try {
      await confirmSignUp({ username: email, confirmationCode: otpCode });
      await signIn({ username: email, password });
      setStep(3);
    } catch (err) {
      setError(friendlyError(err));
    } finally {
      setIsLoading(false);
    }
  };

  const handleResend = async () => {
    setError(null);
    setResendMessage(null);
    setIsResending(true);
    try {
      await resendSignUpCode({ username: email });
      setResendMessage(t('register_parent.code_resent'));
    } catch (err) {
      setError(friendlyError(err));
    } finally {
      setIsResending(false);
    }
  };

  // ── Step 3: POST /students → init → /student ────────────────────────────────
  const handleProfile = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!displayName.trim() || !country || !schoolYear) return;
    setError(null);
    setIsLoading(true);
    try {
      await apiFetch('/students', {
        method: 'POST',
        body: JSON.stringify({
          display_name: displayName.trim(),
          country,
          school_year: schoolYear,
          consent_account: true,
          consent_ai: consentAi,
          consent_curriculum: consentCurriculum,
          consent_version: CONSENT_VERSION,
        }),
      });
      await init();
      navigate('/student', { replace: true });
    } catch (err) {
      setError(friendlyError(err));
    } finally {
      setIsLoading(false);
    }
  };

  const handleBack = () => {
    if (step === 3) { setStep(2); return; }
    if (step === 2) { setStep(1); return; }
    navigate('/login');
  };

  return (
    <div className="page-center">
      <div className="phone-mockup">
        <GlobalHeader showBack onBack={handleBack} customTitle={t('register_university.title')} />
        <div className="app-content" style={{ paddingBottom: '24px' }}>

          {/* ── Step progress bar ── */}
          <div style={{ display: 'flex', gap: '6px', marginBottom: '24px' }}>
            {([1, 2, 3] as const).map((s) => (
              <div
                key={s}
                style={{
                  flex: 1, height: '4px', borderRadius: '2px',
                  background: step >= s ? 'var(--primary)' : 'var(--border)',
                  transition: 'background 0.2s',
                }}
              />
            ))}
          </div>

          {/* ── Step 1: Email + Password ── */}
          {step === 1 && (
            <>
              <h1 style={{ fontSize: '1.5rem', fontWeight: 700, color: 'var(--text-dark)', margin: '0 0 6px' }}>
                {t('register_university.title')}
              </h1>
              <p style={{ fontSize: '0.875rem', color: 'var(--text-muted)', margin: '0 0 24px', lineHeight: 1.5 }}>
                {t('register_university.step1_desc')}
              </p>

              <div className="card">
                <form onSubmit={handleSignUp} style={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>
                  <div className="form-group">
                    <label htmlFor="email">{t('register_parent.email_address_label')}</label>
                    <input
                      id="email"
                      type="email"
                      className="input"
                      placeholder={t('register_university.email_placeholder')}
                      autoComplete="email"
                      value={email}
                      onChange={(e) => setEmail(e.target.value)}
                      disabled={isLoading}
                      required
                    />
                  </div>

                  <PasswordInput
                    id="password"
                    label={t('auth.password_label')}
                    placeholder={t('register_parent.password_placeholder')}
                    autoComplete="new-password"
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    disabled={isLoading}
                    required
                  />

                  {error && (
                    <p style={{
                      color: 'var(--danger)', fontSize: '0.85rem', margin: 0,
                      padding: '10px 14px', background: '#FEF2F2', borderRadius: '8px', lineHeight: 1.5,
                    }}>
                      ⚠️ {error}
                    </p>
                  )}

                  <button
                    type="submit"
                    className="btn btn-primary"
                    disabled={isLoading || !email.trim() || !password}
                    style={{ width: '100%', marginTop: '4px' }}
                  >
                    {isLoading ? t('register_parent.creating_account') : t('register_parent.create_account_btn')}
                  </button>
                </form>
              </div>
            </>
          )}

          {/* ── Step 2: OTP verification ── */}
          {step === 2 && (
            <>
              <h1 style={{ fontSize: '1.5rem', fontWeight: 700, color: 'var(--text-dark)', margin: '0 0 6px' }}>
                {t('register_parent.check_email_heading')}
              </h1>
              <p style={{ fontSize: '0.875rem', color: 'var(--text-muted)', margin: '0 0 32px', lineHeight: 1.5 }}>
                {t('register.code_sent')} <strong>{email}</strong>.
              </p>

              <form onSubmit={handleVerify} style={{ display: 'flex', flexDirection: 'column', gap: '24px' }}>
                <input
                  type="text"
                  inputMode="numeric"
                  maxLength={6}
                  className="input"
                  placeholder="000000"
                  value={otpCode}
                  onChange={(e) => {
                    setOtpCode(e.target.value.replace(/\D/g, '').slice(0, 6));
                    setError(null);
                    setResendMessage(null);
                  }}
                  disabled={isLoading}
                  style={{
                    fontSize: '2rem', letterSpacing: '4px',
                    textAlign: 'center', padding: '16px', fontWeight: 700,
                  }}
                  autoFocus
                />

                {error && (
                  <p style={{
                    color: 'var(--danger)', fontSize: '0.85rem', margin: 0,
                    padding: '10px 14px', background: '#FEF2F2', borderRadius: '8px', lineHeight: 1.5,
                  }}>
                    ⚠️ {error}
                  </p>
                )}

                {resendMessage && !error && (
                  <p style={{
                    color: 'var(--success, #059669)', fontSize: '0.85rem', margin: 0,
                    padding: '10px 14px', background: '#ECFDF5', borderRadius: '8px', lineHeight: 1.5,
                  }}>
                    ✓ {resendMessage}
                  </p>
                )}

                <button
                  type="submit"
                  className="btn btn-primary"
                  disabled={isLoading || otpCode.length < 6}
                  style={{ width: '100%' }}
                >
                  {isLoading ? t('register_parent.verifying') : t('register_university.verify_login_btn')}
                </button>

                <button
                  type="button"
                  className="btn btn-outline"
                  onClick={handleResend}
                  disabled={isLoading || isResending}
                  style={{ width: '100%', fontSize: '0.875rem' }}
                >
                  {isResending ? t('register_parent.resending') : t('auth.resend_code')}
                </button>
              </form>
            </>
          )}

          {/* ── Step 3: Academic profile + consents ── */}
          {step === 3 && (
            <>
              <h1 style={{ fontSize: '1.5rem', fontWeight: 700, color: 'var(--text-dark)', margin: '0 0 6px' }}>
                {t('register_university.almost_done_heading')}
              </h1>
              <p style={{ fontSize: '0.875rem', color: 'var(--text-muted)', margin: '0 0 24px', lineHeight: 1.5 }}>
                {t('register_university.step3_desc')}
              </p>

              <div className="card">
                <form onSubmit={handleProfile} style={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>
                  <div className="form-group">
                    <label htmlFor="displayName">{t('register_parent.first_name_label')}</label>
                    <input
                      id="displayName"
                      type="text"
                      className="input"
                      placeholder={t('register_university.name_placeholder')}
                      autoComplete="given-name"
                      value={displayName}
                      onChange={(e) => setDisplayName(e.target.value)}
                      disabled={isLoading}
                      required
                    />
                  </div>

                  <div className="form-group">
                    <label htmlFor="country">{t('register_student.country_label')}</label>
                    <select
                      id="country"
                      className="input"
                      value={country}
                      onChange={(e) => setCountry(e.target.value)}
                      disabled={isLoading}
                      required
                    >
                      <option value="">{t('register_university.select_country_placeholder')}</option>
                      {COUNTRIES.map((c) => (
                        <option key={c.code} value={c.code}>{c.flag} {c.name}</option>
                      ))}
                    </select>
                  </div>

                  <div className="form-group">
                    <label htmlFor="schoolYear">{t('register_student.year_label')}</label>
                    <select
                      id="schoolYear"
                      className="input"
                      value={schoolYear}
                      onChange={(e) => setSchoolYear(e.target.value)}
                      disabled={isLoading}
                      required
                    >
                      <option value="">{t('register_university.select_year_placeholder')}</option>
                      {UNIVERSITY_YEARS.map((y) => (
                        <option key={y} value={y}>{y}</option>
                      ))}
                    </select>
                  </div>

                  <div style={{ background: 'var(--bg-light)', borderRadius: '10px', padding: '16px' }}>
                    <p style={{ fontSize: '0.85rem', fontWeight: 600, color: 'var(--text-dark)', margin: '0 0 12px' }}>
                      {t('register.privacy_choices')}
                    </p>

                    <label style={{ display: 'flex', alignItems: 'flex-start', gap: '10px', marginBottom: '10px', cursor: 'pointer' }}>
                      <input
                        type="checkbox"
                        checked={consentAi}
                        onChange={(e) => setConsentAi(e.target.checked)}
                        style={{ marginTop: '2px', flexShrink: 0 }}
                      />
                      <span style={{ fontSize: '0.825rem', color: 'var(--text-dark)', lineHeight: 1.5 }}>
                        {t('register.consent_ai_label')}
                      </span>
                    </label>

                    <label style={{ display: 'flex', alignItems: 'flex-start', gap: '10px', cursor: 'pointer' }}>
                      <input
                        type="checkbox"
                        checked={consentCurriculum}
                        onChange={(e) => setConsentCurriculum(e.target.checked)}
                        style={{ marginTop: '2px', flexShrink: 0 }}
                      />
                      <span style={{ fontSize: '0.825rem', color: 'var(--text-dark)', lineHeight: 1.5 }}>
                        {t('register_student.consent_curriculum_label')}
                      </span>
                    </label>

                    <p style={{ fontSize: '0.75rem', color: 'var(--text-muted)', margin: '12px 0 0', lineHeight: 1.5 }}>
                      {t('register.tos_prefix')} <strong>{t('register.tos_terms')}</strong> {t('register.tos_suffix', { region: t(getRegionI18nKey()) })}
                    </p>
                  </div>

                  {error && (
                    <p style={{
                      color: 'var(--danger)', fontSize: '0.85rem', margin: 0,
                      padding: '10px 14px', background: '#FEF2F2', borderRadius: '8px', lineHeight: 1.5,
                    }}>
                      ⚠️ {error}
                    </p>
                  )}

                  <button
                    type="submit"
                    className="btn btn-primary"
                    disabled={isLoading || !displayName.trim() || !country || !schoolYear}
                    style={{ width: '100%', marginTop: '4px' }}
                  >
                    {isLoading ? t('register_parent.setting_up') : t('register_parent.go_dashboard_btn')}
                  </button>
                </form>
              </div>
            </>
          )}

        </div>
      </div>
    </div>
  );
}
